import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Drawer, Input, Button, Spin, Empty, Tabs } from 'antd';
import { SearchOutlined, PlusOutlined, RobotOutlined, FileTextOutlined, AppstoreOutlined, BarsOutlined } from '@ant-design/icons';
import { usePrompts, useSystemPrompts, useCreatePrompt, useCreateSystemPrompt } from '../../../api/promptApi';
import PromptSidePanel from './PromptSidePanel';
import SavePromptModal from './SavePromptModal';

const PromptLibraryDrawer = ({ visible, onClose, onCopyToCanvas }) => {
    // State
    const [activeTab, setActiveTab] = useState('prompts'); // 'prompts' | 'system'
    const [searchText, setSearchText] = useState('');
    const [selectedPrompt, setSelectedPrompt] = useState(null);
    const [viewMode, setViewMode] = useState('grid');
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [drawerWidth, setDrawerWidth] = useState(420);

    // Resize handling
    const isResizing = useRef(false);

    const handleMouseDown = (e) => {
        e.preventDefault();
        isResizing.current = true;
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = useCallback((e) => {
        if (!isResizing.current) return;
        const newWidth = window.innerWidth - e.clientX;
        if (newWidth >= 340 && newWidth <= 1200) {
            setDrawerWidth(newWidth);
        }
    }, []);

    const handleMouseUp = useCallback(() => {
        isResizing.current = false;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    }, [handleMouseMove]);

    useEffect(() => {
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [handleMouseMove, handleMouseUp]);

    // Initial Data Fetch
    const { data: prompts = [], isLoading: promptsLoading } = usePrompts();
    const { data: systemPrompts = [], isLoading: systemPromptsLoading } = useSystemPrompts();

    // Mutations
    const createPromptMutation = useCreatePrompt();
    const createSystemPromptMutation = useCreateSystemPrompt();

    // Derived Data
    const currentData = activeTab === 'prompts' ? prompts : systemPrompts;
    const isLoading = activeTab === 'prompts' ? promptsLoading : systemPromptsLoading;

    // Filter
    const filteredData = Array.isArray(currentData) ? currentData.filter(item => {
        const lowerSearch = searchText.toLowerCase();
        const name = item?.name || '';
        const tags = Array.isArray(item?.tags) ? item.tags : [];

        return (
            name.toLowerCase().includes(lowerSearch) ||
            tags.some(tag => (tag || '').toLowerCase().includes(lowerSearch))
        );
    }) : [];

    // Handlers
    const handlePromptClick = (prompt) => {
        setSelectedPrompt(prompt);
    };

    const handleCreateSave = async (values) => {
        const { files = [], ...rest } = values;
        const payload = {
            ...rest,
            files,
            content: rest.content || "Write your prompt here...",
            system: { id: 'system', email: 'system@pxlplygrnd.com' }
        };

        if (activeTab === 'prompts') {
            await createPromptMutation.mutateAsync(payload);
        } else {
            await createSystemPromptMutation.mutateAsync(payload);
        }
    };

    return (
        <>
            <Drawer
                title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <span style={{ color: '#c9d1d9', fontSize: 12, fontWeight: 600, letterSpacing: '0.5px' }}>PROMPT LIBRARY</span>
                        </div>
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            {/* View Mode Toggle */}
                            <div style={{ display: 'flex', gap: 2 }}>
                                <div
                                    onClick={() => setViewMode('grid')}
                                    style={{
                                        width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        background: viewMode === 'grid' ? 'rgba(24, 144, 255, 0.1)' : 'transparent',
                                        borderRadius: 4, cursor: 'pointer', transition: 'all 0.2s',
                                        color: viewMode === 'grid' ? '#1890ff' : '#8b949e'
                                    }}
                                >
                                    <AppstoreOutlined style={{ fontSize: 14 }} />
                                </div>
                                <div
                                    onClick={() => setViewMode('list')}
                                    style={{
                                        width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        background: viewMode === 'list' ? 'rgba(24, 144, 255, 0.1)' : 'transparent',
                                        borderRadius: 4, cursor: 'pointer', transition: 'all 0.2s',
                                        color: viewMode === 'list' ? '#1890ff' : '#8b949e'
                                    }}
                                >
                                    <BarsOutlined style={{ fontSize: 14 }} />
                                </div>
                            </div>
                        </div>
                    </div>
                }
                closable={false}
                placement="right"
                onClose={onClose}
                open={visible}
                getContainer={false}
                mask={true}
                maskStyle={{ background: 'transparent' }}
                width={drawerWidth}
                style={{ position: 'absolute', background: '#0d1117', borderLeft: '1px solid #30363d' }}
                headerStyle={{ borderBottom: '1px solid #30363d', background: '#161b22', padding: '16px 24px', height: 60, display: 'flex', alignItems: 'center' }}
                bodyStyle={{ background: '#0d1117', padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            >
                {/* Resizer Handle */}
                <div
                    onMouseDown={handleMouseDown}
                    style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 6,
                        cursor: 'col-resize',
                        zIndex: 100,
                        background: 'transparent',
                        transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.background = 'rgba(24, 144, 255, 0.3)'}
                    onMouseLeave={(e) => {
                        if (!isResizing.current) e.target.style.background = 'transparent';
                    }}
                />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    {/* Tabs */}
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid #30363d', background: '#161b22' }}>
                        <Tabs
                            activeKey={activeTab}
                            onChange={(key) => { setActiveTab(key); setSelectedPrompt(null); }}
                            size="small"
                            items={[
                                { key: 'prompts', label: <span><FileTextOutlined /> Prompts</span> },
                                { key: 'system', label: <span><RobotOutlined /> System Prompts</span> }
                            ]}
                            tabBarStyle={{ margin: 0, color: '#8b949e' }}
                        />
                    </div>

                    {/* Toolbar */}
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid #30363d', display: 'flex', justifyContent: 'space-between', gap: 12, background: '#161b22' }}>
                        <Input
                            prefix={<SearchOutlined style={{ color: '#8b949e' }} />}
                            placeholder={`Search ${activeTab === 'prompts' ? 'prompts' : 'system prompts'}...`}
                            onChange={e => setSearchText(e.target.value)}
                            style={{ flex: 1, background: '#0d1117', borderColor: '#30363d' }}
                            allowClear
                        />
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setIsCreateModalOpen(true)}
                        >
                            New
                        </Button>
                    </div>

                    {/* Content Area - Split View */}
                    <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
                        {/* Left Side: List */}
                        <div style={{
                            flex: selectedPrompt ? '0 0 350px' : '1',
                            overflowY: 'auto',
                            padding: 16,
                            background: '#0d1117',
                            transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                            borderRight: selectedPrompt ? '1px solid #30363d' : 'none'
                        }}>
                            {isLoading ? (
                                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 60 }}>
                                    <Spin size="large" />
                                </div>
                            ) : filteredData.length === 0 ? (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={<span style={{ color: '#8b949e' }}>No prompts found</span>}
                                    style={{ marginTop: 60 }}
                                >
                                    <Button onClick={() => setIsCreateModalOpen(true)}>Create your first prompt</Button>
                                </Empty>
                            ) : (
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: viewMode === 'grid' && !selectedPrompt ? 'repeat(auto-fill, minmax(180px, 1fr))' : '1fr',
                                    gap: 12
                                }}>
                                    {filteredData.map(item => (
                                        <div
                                            key={item._id}
                                            onClick={() => handlePromptClick(item)}
                                            style={{
                                                background: selectedPrompt?._id === item._id ? '#1f242c' : '#161b22',
                                                border: selectedPrompt?._id === item._id ? '1px solid #58a6ff' : '1px solid #30363d',
                                                borderRadius: 8,
                                                padding: 12,
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                height: viewMode === 'grid' && !selectedPrompt ? 140 : 'auto',
                                                position: 'relative',
                                                overflow: 'hidden'
                                            }}
                                            className="prompt-card"
                                            onMouseEnter={e => {
                                                if (selectedPrompt?._id !== item._id) {
                                                    e.currentTarget.style.borderColor = '#58a6ff';
                                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                                }
                                            }}
                                            onMouseLeave={e => {
                                                if (selectedPrompt?._id !== item._id) {
                                                    e.currentTarget.style.borderColor = '#30363d';
                                                    e.currentTarget.style.transform = 'none';
                                                }
                                            }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, position: 'relative', zIndex: 1 }}>
                                                <div style={{ fontWeight: 600, color: '#e6edf3', fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                                                    {item.name}
                                                </div>
                                            </div>

                                            {/* Thumbnail */}
                                            {item.mediaUrls && item.mediaUrls.length > 0 && viewMode === 'grid' && !selectedPrompt && (
                                                <div style={{
                                                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                                    backgroundImage: `url(${item.mediaUrls[0].url})`,
                                                    backgroundSize: 'cover', backgroundPosition: 'center',
                                                    opacity: 0.2, borderRadius: 8, zIndex: 0
                                                }} />
                                            )}

                                            <div style={{ color: '#8b949e', fontSize: 11, marginBottom: 8, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', flex: 1, position: 'relative', zIndex: 1 }}>
                                                {item.description || item.content}
                                            </div>

                                            <div style={{ marginTop: 'auto', display: 'flex', gap: 4, flexWrap: 'wrap', overflow: 'hidden', height: 20, position: 'relative', zIndex: 1 }}>
                                                {(item.tags || []).slice(0, 2).map(tag => (
                                                    <span key={tag} style={{ fontSize: 10, background: 'rgba(56, 139, 253, 0.1)', color: '#58a6ff', padding: '2px 6px', borderRadius: 99 }}>
                                                        {tag}
                                                    </span>
                                                ))}
                                                {(item.tags || []).length > 2 && (
                                                    <span style={{ fontSize: 10, color: '#8b949e' }}>+{(item.tags || []).length - 2}</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Right Side: Details */}
                        <div style={{
                            flex: selectedPrompt ? '1' : '0',
                            width: selectedPrompt ? 'auto' : 0,
                            opacity: selectedPrompt ? 1 : 0,
                            overflow: 'hidden',
                            transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                            background: '#161b22',
                            position: 'relative'
                        }}>
                            {selectedPrompt && (
                                <div style={{ height: '100%', width: '100%', position: 'absolute', top: 0, left: 0 }}>
                                    <PromptSidePanel
                                        prompt={selectedPrompt}
                                        isOpen={true}
                                        onClose={() => setSelectedPrompt(null)}
                                        onCopyToCanvas={onCopyToCanvas}
                                        type={activeTab}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Drawer>

            {/* Create Modal */}
            <SavePromptModal
                visible={isCreateModalOpen}
                onCancel={() => setIsCreateModalOpen(false)}
                onSave={handleCreateSave}
                title={`Create New ${activeTab === 'prompts' ? 'Prompt' : 'System Prompt'}`}
                initialValues={{}}
            />
        </>
    );
};

export default PromptLibraryDrawer;
